import React, { useMemo } from 'react';
import hexToRgbChannels from '../../assets/js/hexToRgbChannels';
import useElementWidth from './useElementWidth';

const WordCloud = ({ words = [], height = 280, color = '#7C3AED', maxWords = 40, className = '' }) => {
  const rgb = hexToRgbChannels(color) || '124, 58, 237';
  const [ref, width] = useElementWidth();
  const compact = width > 0 && width < 480;
  const boxHeight = compact ? Math.round(height * 0.85) : height;

  const items = useMemo(() => {
    if (!width) return [];
    const sorted = [...words].filter((w) => w && w.word).sort((a, b) => b.count - a.count).slice(0, maxWords);
    if (!sorted.length) return [];
    const max = sorted[0].count;
    const min = sorted[sorted.length - 1].count || 1;
    const cx = width / 2;
    const cy = boxHeight / 2;
    const minFont = compact ? 12 : 14;
    const fontRange = compact ? 22 : 38;
    const placed = [];

    return sorted.reduce((acc, w) => {
      const weight = max === min ? 0.6 : (w.count - min) / (max - min);
      const fontSize = Math.round(minFont + Math.pow(weight, 0.6) * fontRange);
      const boxW = w.word.length * fontSize * 0.56 + 8;
      const boxH = fontSize * 1.1;
      let spot = null;

      for (let step = 0; step < 2400; step += 1) {
        const angle = step * 0.18;
        const radius = 1.3 * angle;
        const x = cx + radius * Math.cos(angle) * (width / boxHeight > 1.6 ? 1.4 : 1);
        const y = cy + radius * Math.sin(angle);
        if (x - boxW / 2 < 4 || x + boxW / 2 > width - 4 || y - boxH / 2 < 4 || y + boxH / 2 > boxHeight - 4) continue;
        const collides = placed.some((p) =>
          Math.abs(x - p.x) < (boxW + p.w) / 2 + 4 && Math.abs(y - p.y) < (boxH + p.h) / 2 + 2
        );
        if (!collides) {
          spot = { x, y };
          break;
        }
      }

      if (!spot) return acc;
      placed.push({ ...spot, w: boxW, h: boxH });
      acc.push({ word: w.word, count: w.count, fontSize, x: spot.x, y: spot.y, alpha: 0.45 + weight * 0.55 });
      return acc;
    }, []);
  }, [words, width, boxHeight, compact, maxWords]);

  return (
    <div ref={ref} className={className} style={{ width: '100%', minHeight: width ? undefined : boxHeight }}>
      {items.length > 0 && (
        <svg
          width={width}
          height={boxHeight}
          viewBox={`0 0 ${width} ${boxHeight}`}
          role="img"
          aria-label={items.slice(0, 10).map((it) => `${it.word} (${it.count})`).join(', ')}
          style={{ display: 'block' }}
        >
          {items.map((it) => (
            <text
              key={it.word}
              x={it.x}
              y={it.y}
              fontSize={it.fontSize}
              fill={`rgba(${rgb}, ${it.alpha.toFixed(2)})`}
              textAnchor="middle"
              dominantBaseline="central"
              fontWeight={it.alpha > 0.8 ? 800 : 600}
              fontFamily="var(--hero-font-body)"
            >
              <title>{`${it.word}: ${it.count}`}</title>
              {it.word}
            </text>
          ))}
        </svg>
      )}
    </div>
  );
};

export default WordCloud;
export { WordCloud };
